import { useState, useEffect } from 'react';
import CesiumViewer from './components/CesiumViewer';
import Sidebar from './components/Sidebar';
import ChatPanel from './components/ChatPanel';
import RackEditor from './components/RackEditor';
import RackPreview3D from './components/RackPreview3D';
import { useSitesStore } from './stores/sitesStore';
import { useRackEditorStore } from './stores/rackEditorStore';
import { API_URL } from './config/api';

interface User {
  email: string;
  name: string;
  picture: string;
}

function App() {
  const [chatOpen, setChatOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { fetchSites } = useSitesStore();
  const { isOpen: isRackEditorOpen } = useRackEditorStore();

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.authenticated) {
          setUser(data.user);
          fetchSites();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [fetchSites]);

  const handleLogin = () => {
    window.location.href = `${API_URL}/auth/google/login`;
  };

  const handleLogout = () => {
    window.location.href = `${API_URL}/auth/logout`;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>Kencove Site Plans</h1>
          <p>3D Facility Visualization Platform</p>
          <button className="login-button" onClick={handleLogin}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>
          <p className="login-note">Restricted to @kencove.com accounts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        <CesiumViewer />
        <div className="viewer-controls">
          <div className="user-menu">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="user-avatar" />
            )}
            <span className="user-name">{user.name}</span>
            <button className="logout-button" onClick={handleLogout} title="Logout">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
          <button
            className="chat-toggle"
            onClick={() => setChatOpen(!chatOpen)}
            title="AI Assistant"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        </div>
        {chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}
        <RackPreview3D enabled={isRackEditorOpen} />
      </main>
      {isRackEditorOpen && <RackEditor />}
    </div>
  );
}

export default App;
